/*
 * Copyright (C) 2018 - present by OpenGamma Inc. and the OpenGamma group of companies
 *
 * Please see distribution for license.
 */

import { Component } from '@angular/core';
import { Observable } from 'rxjs';
import { UserDetails } from 'app/user-auth/models';
import { select, Store } from '@ngrx/store';
import { State } from 'app/main/store';
import * as fromUserAuth from 'app/user-auth/store/user-auth.selectors';
import * as fromSharedData from 'app/shared-data/store/shared-data.selectors';
import {
  AuditedLoginToTenant,
  LoginToTenant,
  UserLogout
} from 'app/user-auth/store/user-auth.actions';
import { filterUndefined } from 'app/shared/rxjs/rxjs.utils';
import { TenantDetails, AuditRequestReason } from 'app/page-modules/tenant-management/models';
import { PreviewEnvironment, EnvironmentLogin } from 'app/shared-data/models';
import { LoadableState } from '@opengamma/ui';
import { LoadAllTenantDetails, LoadPreviewEnvironments } from 'app/shared-data/store';
import { AuthService } from 'app/user-auth/services/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { TenantLoginReasonPanelComponent } from 'app/shared/components/tenant-login-reason-panel/tenant-login-reason-panel.component';

@Component({
  selector: 'og-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false
})
export class HeaderComponent {
  userDetails$: Observable<UserDetails>;
  userDetailsLoading$: Observable<boolean>;

  tenantDetails$: Observable<LoadableState<TenantDetails[]>>;
  previewEnvironments$: Observable<LoadableState<PreviewEnvironment[]>>;

  constructor(
    private store: Store<State>,
    private authService: AuthService,
    private matDialog: MatDialog
  ) {
    store.dispatch(new LoadAllTenantDetails());
    store.dispatch(new LoadPreviewEnvironments());

    this.userDetails$ = store.pipe(select(fromUserAuth.getUserDetailsData), filterUndefined());
    this.userDetailsLoading$ = store.pipe(select(fromUserAuth.isUserDetailsDataLoading));

    this.tenantDetails$ = store.pipe(select(fromSharedData.getTenants), filterUndefined());
    this.previewEnvironments$ = store.pipe(
      select(fromSharedData.getPreviewEnvironments),
      filterUndefined()
    );
  }

  loginToEnvironment(environmentLogin: EnvironmentLogin): void {
    const tenantDetails = environmentLogin.tenantDetails;
    if (this.authService.requiresLoginReason(tenantDetails)) {
      const ref = this.matDialog.open(TenantLoginReasonPanelComponent);
      ref.componentInstance.saveClick.subscribe((auditDetails: AuditRequestReason) => {
        this.store.dispatch(
          new AuditedLoginToTenant(tenantDetails, environmentLogin.previewEnvironment, auditDetails)
        );
        ref.close();
      });
    } else {
      this.store.dispatch(new LoginToTenant(tenantDetails, environmentLogin.previewEnvironment));
    }
  }

  logout(): void {
    this.store.dispatch(new UserLogout());
  }
}
